// B-136 — "Comité de seguimiento", in Reportes: what the client's monthly follow-up presentation
// shows (Bancamía: transactions and amounts, vault excesses, reversals, cash in transit), for the
// page's period and the one before it, and for the offices chosen on the map — from
// `getCierreOperationReport`. Trends are charts; rankings and the per-office detail are tables,
// paged rather than scrolled inside the page. The page's own header exports the metrics workbook
// and the presentation, from the report this section hands up (`onReporte`).
// Drawn only for a client that declares one: the route answers 404 for any other and nothing is
// shown. The pure halves are `utils/cierreComite.js` and `utils/cierreComitePptx.js`.
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, ComposedChart, Legend, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import {
  ArrowDownRight, ArrowUpRight, ChevronLeft, ChevronRight, Landmark, Repeat2, Truck, Undo2, Vault,
} from "lucide-react";
import { Spinner, cn } from "@mantis-sas/ui";

import { getCierreOperationReport } from "../../../ApiCalls/mantisBusiness";
import { correctionError } from "../../../utils/cierreCorrections";
import { formatMoney } from "../../../utils/formatMoney";
import { getColombiaDateString } from "../../../utils/dateUtils";
import { usePaginaPorClave } from "../../../hooks/usePaginaPorClave";
import { conReintento } from "../../../utils/conReintento";
import {
  diasDelMes,
  etiquetaDePeriodo,
  indicadoresDelMes,
  oficinasPorVolumen,
  pedidoDelComite,
  porRegional,
  porcentaje,
  ranking,
  recaudosYPagos,
  topOficinas,
  transaccionesPorAnio,
  ultimoMesConDatos,
} from "../../../utils/cierreComite";
import { guiaDe } from "../../../utils/cierreTours";

// The platform's primary for this period, a quiet slate for the one before, one hue per regional.
const PRIMARIO = "hsl(var(--primary))";
const ANTERIOR = "#cbd5e1";
const SERIE = ["hsl(var(--primary))", "#3b5ba9", "#6f86c2", "#93a3c4", "#c7d0e2", "#e2e8f0"];
const PELIGRO = "var(--brinks-danger)";
const AVISO = "var(--brinks-warning)";
const EJE = { tickLine: false, axisLine: false, fontSize: 11, stroke: "var(--brinks-muted-foreground)" };
const POR_PAGINA = 15;

const pesos = (n) => `$${formatMoney(Math.round(Number(n) || 0))}`;
const cuenta = (n) => formatMoney(Math.round(Number(n) || 0));
const compacto = (n) => {
  const v = Math.abs(Number(n) || 0);
  if (v >= 1e9) return `${(n / 1e9).toFixed(1).replace(".", ",")} MM`;
  if (v >= 1e8) return `${Math.round(n / 1e6)} M`;
  if (v >= 1e6) return `${(n / 1e6).toFixed(1).replace(".", ",")} M`;
  if (v >= 1e3) return `${Math.round(n / 1e3)} k`;
  return String(Math.round(n));
};

function Variacion({ valor, invertir = false, className }) {
  if (valor === null || valor === undefined) return <span className={cn("text-brinks-muted-foreground", className)}>—</span>;
  // Up is good for volume; for excesses and reversals (`invertir`) up is the bad news.
  const bueno = invertir ? valor < 0 : valor > 0;
  const Icono = valor >= 0 ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={cn("inline-flex items-center gap-0.5 tabular-nums", valor === 0 ? "text-brinks-muted-foreground" : bueno ? "text-brinks-success" : "text-brinks-danger", className)}>
      {valor !== 0 ? <Icono className="h-3.5 w-3.5" /> : null}
      {porcentaje(valor)}
    </span>
  );
}

// A headline figure with its variation and the period's days drawn beneath it.
function Cifra({ icono: Icono, etiqueta, valor, detalle, variacion, invertir, anterior, serie, clave }) {
  return (
    <article {...guiaDe(etiqueta, detalle)} className="flex min-w-0 flex-col overflow-hidden rounded-[14px] border bg-card shadow-brinks-card">
      <div className="flex flex-col gap-1.5 px-4 pt-4">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[12px] font-medium text-brinks-muted-foreground">{etiqueta}</span>
          <span className="grid h-7 w-7 place-items-center rounded-[8px] bg-brinks-surface text-brinks-foreground">
            <Icono className="h-3.5 w-3.5" />
          </span>
        </div>
        <p className="truncate text-[24px] font-semibold leading-tight tabular-nums text-brinks-foreground">{valor}</p>
        <div className="flex flex-wrap items-center justify-between gap-1 text-[11.5px] text-brinks-muted-foreground">
          <span className="min-w-0 truncate">{detalle}</span>
          <span className="inline-flex items-center gap-1">
            <Variacion valor={variacion} invertir={invertir} />
            <span>vs {anterior}</span>
          </span>
        </div>
      </div>
      <div className="mt-2 h-12">
        {serie?.length > 1 ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={serie} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
              <Area type="monotone" dataKey={clave} stroke={invertir ? PELIGRO : PRIMARIO} strokeWidth={1.5} fill={invertir ? "rgba(220,38,38,0.08)" : "rgba(15,23,42,0.07)"} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        ) : null}
      </div>
    </article>
  );
}

function Tarjeta({ titulo, subtitulo, extra, children, className, cuerpo = "p-4" }) {
  return (
    <section {...guiaDe(titulo, subtitulo)} className={cn("flex min-w-0 flex-col overflow-hidden rounded-[14px] border bg-card shadow-brinks-card", className)} aria-label={titulo}>
      <header className="flex flex-wrap items-start justify-between gap-2 px-4 pb-1 pt-3.5">
        <div className="min-w-0">
          <h3 className="text-[14px] font-semibold text-brinks-foreground">{titulo}</h3>
          {subtitulo ? <p className="mt-0.5 text-[12px] text-brinks-muted-foreground">{subtitulo}</p> : null}
        </div>
        {extra}
      </header>
      <div className={cn("min-w-0 flex-1", cuerpo)}>{children}</div>
    </section>
  );
}

// A two-option switch in the platform's shape (the panel's period chips).
function Selector({ opciones, valor, onChange, etiqueta }) {
  return (
    <div className="flex items-center gap-0.5 rounded-[9px] border bg-card p-0.5" role="group" aria-label={etiqueta}>
      {opciones.map((o) => (
        <button
          key={o.valor}
          type="button"
          aria-pressed={valor === o.valor}
          onClick={() => onChange(o.valor)}
          className={cn(
            "rounded-[7px] px-2.5 py-1 text-[11.5px] font-medium capitalize transition",
            valor === o.valor ? "bg-primary text-primary-foreground" : "text-brinks-muted-foreground hover:text-brinks-foreground"
          )}
        >
          {o.etiqueta}
        </button>
      ))}
    </div>
  );
}

// A section's heading inside the committee: a number, the deck's own words and a hairline.
const Bloque = ({ numero, titulo }) => (
  <div className="flex items-center gap-2 pt-2">
    <span className="grid h-5 w-5 place-items-center rounded-full bg-primary text-[10.5px] font-semibold text-primary-foreground">{numero}</span>
    <h3 className="text-[12px] font-semibold uppercase tracking-[0.06em] text-brinks-muted-foreground">{titulo}</h3>
    <span className="h-px flex-1 bg-brinks-border" />
  </div>
);

const Vacio = ({ children }) => (
  <p className="grid h-full min-h-[120px] place-items-center rounded-[10px] border border-dashed text-center text-[12px] text-brinks-muted-foreground">{children}</p>
);

// The office as a short label: without "Bancamia" in front or the "- CORRESPONSAL EPAGO" tail.
const nombreCorto = (o) => String(o.oficina || "")
  .replace(/\s*-?\s*(CORRESPONSAL|CORR\.).*$/i, "")
  .replace(/^BANCAMIA\s+/i, "")
  .trim();

// The same tooltip everywhere: the label, then each series with its own format.
function Burbuja({ active, payload, label, formatos = {}, etiqueta }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-[10px] border bg-card px-3 py-2 text-[11.5px] shadow-brinks-card">
      <p className="mb-1 font-medium text-brinks-foreground">{etiqueta ? etiqueta(label, payload) : label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="flex items-center gap-2 text-brinks-muted-foreground">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color || p.payload?.fill }} />
          <span>{p.name}</span>
          <span className="ml-auto pl-3 font-medium tabular-nums text-brinks-foreground">{(formatos[p.dataKey] || cuenta)(p.value)}</span>
        </p>
      ))}
    </div>
  );
}

const TH = "py-2 pr-3 text-[10.5px] font-medium uppercase tracking-wide text-brinks-muted-foreground";
const TD = "py-2 pr-3";

// B-138 — an office's row narrows the Dashboard to it: a click, or Enter on the focused row.
const FILA_ELEGIBLE = "cursor-pointer hover:bg-brinks-surface-hover focus:bg-brinks-surface-hover focus:outline-none";
const elegible = (onElegir, o) => (onElegir
  ? {
    tabIndex: 0,
    title: `Ver solo ${o.oficina} en el Dashboard`,
    onClick: () => onElegir(o),
    onKeyDown: (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onElegir(o);
      }
    },
  }
  : {});

// A ranking as a table — position, office, regional, how many and how much — with the period and
// the one before it to choose from, as the deck shows them side by side.
function Ranking({ reporte, titulo, subtitulo, cantidad, valor, columnaCantidad, vacio, limite = 10, tono, onElegir }) {
  const [periodo, setPeriodo] = useState("actual");
  const datos = useMemo(() => ranking(reporte, periodo, cantidad, valor, limite), [reporte, periodo, cantidad, valor, limite]);
  const maximo = datos.filas.length ? datos.filas[0].cantidad : 0;
  return (
    <Tarjeta
      titulo={titulo}
      subtitulo={subtitulo}
      extra={(
        <Selector
          etiqueta={`Periodo de ${titulo}`}
          valor={periodo}
          onChange={setPeriodo}
          opciones={[
            { valor: "anterior", etiqueta: etiquetaDePeriodo(reporte, "anterior", { corta: true }) },
            { valor: "actual", etiqueta: etiquetaDePeriodo(reporte, "actual", { corta: true }) },
          ]}
        />
      )}
    >
      {datos.filas.length ? (
        <table className="w-full text-[12px]">
          <thead>
            <tr className="border-b text-left">
              <th className={TH}>Oficina</th>
              <th className={cn(TH, "hidden sm:table-cell")}>Regional</th>
              <th className={cn(TH, "text-right")}>{columnaCantidad}</th>
              <th className={cn(TH, "pr-0 text-right")}>Valor</th>
            </tr>
          </thead>
          <tbody>
            {datos.filas.map((o, i) => (
              <tr key={o.codigo} className={cn("border-b last:border-0", onElegir && FILA_ELEGIBLE)} {...elegible(onElegir, o)}>
                <td className={TD}>
                  <span className="mr-2 inline-block w-4 text-right tabular-nums text-brinks-muted-foreground">{i + 1}</span>
                  <span className="font-medium text-brinks-foreground">{o.oficina}</span>
                  <span className="ml-1.5 text-[10.5px] text-brinks-muted-foreground">{o.codigo}</span>
                </td>
                <td className={cn(TD, "hidden text-brinks-muted-foreground sm:table-cell")}>{o.region || "—"}</td>
                <td className={cn(TD, "text-right")}>
                  {/* A thin mark of how it compares with the first: the number is the figure. */}
                  <span className="inline-flex items-center gap-2">
                    <span className="hidden h-1 w-10 overflow-hidden rounded-full bg-brinks-surface md:inline-block">
                      <span className="block h-full rounded-full" style={{ width: `${maximo ? (o.cantidad / maximo) * 100 : 0}%`, background: tono }} />
                    </span>
                    <span className="w-6 tabular-nums">{cuenta(o.cantidad)}</span>
                  </span>
                </td>
                <td className={cn(TD, "pr-0 text-right tabular-nums")}>{pesos(o.valor)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t text-[12px] font-semibold">
              <td className={TD}>Total de las {datos.filas.length} primeras{datos.oficinas > datos.filas.length ? ` (de ${datos.oficinas})` : ""}</td>
              <td className={cn(TD, "hidden sm:table-cell")} />
              <td className={cn(TD, "text-right tabular-nums")}>{cuenta(datos.total.cantidad)}</td>
              <td className={cn(TD, "pr-0 text-right tabular-nums")}>{pesos(datos.total.valor)}</td>
            </tr>
          </tfoot>
        </table>
      ) : <Vacio>{vacio}</Vacio>}
    </Tarjeta>
  );
}

// A table's pager: previous/next and "a–b de n", inside the card, so the page never scrolls twice.
function Pagina({ pagina, total, onPagina }) {
  const paginas = Math.max(1, Math.ceil(total / POR_PAGINA));
  if (paginas <= 1) return null;
  const desde = (pagina - 1) * POR_PAGINA + 1;
  return (
    <div className="mt-2 flex items-center justify-between gap-2 border-t pt-2 text-[11.5px] text-brinks-muted-foreground">
      <span className="tabular-nums">{desde}–{Math.min(total, desde + POR_PAGINA - 1)} de {total}</span>
      <span className="flex items-center gap-1">
        <button type="button" aria-label="Página anterior" disabled={pagina <= 1} onClick={() => onPagina(pagina - 1)} className="grid h-7 w-7 place-items-center rounded-[7px] border bg-card disabled:opacity-40">
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
        <span className="min-w-[48px] text-center tabular-nums">{pagina} / {paginas}</span>
        <button type="button" aria-label="Página siguiente" disabled={pagina >= paginas} onClick={() => onPagina(pagina + 1)} className="grid h-7 w-7 place-items-center rounded-[7px] border bg-card disabled:opacity-40">
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </span>
    </div>
  );
}

// "Volumen y montos por oficina": every office, the period before and this one, paged.
function PorOficina({ reporte, onElegir }) {
  const [campo, setCampo] = useState("tx");
  const filas = useMemo(() => oficinasPorVolumen(reporte, campo), [reporte, campo]);
  const [pagina, setPagina] = usePaginaPorClave(`${reporte.from}|${reporte.to}|${(reporte.selectedOffices || []).join(",")}|${campo}`);
  const ant = etiquetaDePeriodo(reporte, "anterior", { corta: true });
  const act = etiquetaDePeriodo(reporte, "actual", { corta: true });
  const formato = campo === "tx" ? cuenta : pesos;
  const visibles = filas.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA);
  return (
    <Tarjeta
      titulo={campo === "tx" ? "Volumen de transacciones por oficina" : "Montos por oficina"}
      subtitulo={`${etiquetaDePeriodo(reporte, "anterior")} frente a ${etiquetaDePeriodo(reporte)}, de mayor a menor como en el comité.`}
      extra={<Selector etiqueta="Volumen o montos" valor={campo} onChange={setCampo} opciones={[{ valor: "tx", etiqueta: "Volumen" }, { valor: "valor", etiqueta: "Montos" }]} />}
    >
      {filas.length ? (
        <>
          <table className="w-full text-[12px]">
            <thead>
              <tr className="border-b text-left">
                <th className={TH}>Oficina</th>
                <th className={cn(TH, "hidden sm:table-cell")}>Regional</th>
                <th className={cn(TH, "text-right capitalize")}>{ant}</th>
                <th className={cn(TH, "text-right capitalize")}>{act}</th>
                <th className={cn(TH, "pr-0 text-right")}>Variación</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((o) => (
                <tr key={o.codigo} className={cn("border-b last:border-0 hover:bg-brinks-surface-hover", onElegir && FILA_ELEGIBLE)} {...elegible(onElegir, o)}>
                  <td className={TD}>
                    <span className="font-medium text-brinks-foreground">{o.oficina}</span>
                    <span className="ml-1.5 text-[10.5px] text-brinks-muted-foreground">{o.codigo}</span>
                  </td>
                  <td className={cn(TD, "hidden text-brinks-muted-foreground sm:table-cell")}>{o.region || "—"}</td>
                  <td className={cn(TD, "text-right tabular-nums text-brinks-muted-foreground")}>{formato(o.anterior)}</td>
                  <td className={cn(TD, "text-right tabular-nums")}>{formato(o.actual)}</td>
                  <td className={cn(TD, "pr-0 text-right")}><Variacion valor={o.variacion} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagina pagina={pagina} total={filas.length} onPagina={setPagina} />
        </>
      ) : <Vacio>Sin transacciones en estos dos periodos.</Vacio>}
    </Tarjeta>
  );
}

/**
 * @param {object} props
 * @param {?{desde: string, hasta: string}} props.periodo  the page's period; null ("Todo") reads the
 *        current month, or the last one with transactions
 * @param {?string[]} props.oficinas  the office codes chosen on the map; null for every office
 * @param {Function} [props.onReporte] receives the report on screen (or null), for the page's exports
 * @param {Function} [props.onElegir]  B-138 — `{ tipo, etiqueta, codigos }`: narrows the page to an
 *        office (a ranking's row, a top bar) or a regional (its slice), the way the map does
 * @param {Function} [props.onAbrirDia] B-138 — `'YYYY-MM-DD'`: a day's bar opens that day's closings
 */
export function ComiteSeguimiento({
  token, mantisBusinessId, clientCode, periodo = null, oficinas = null, onReporte = () => {}, onElegir = null, onAbrirDia = null,
}) {
  const hoy = useMemo(() => getColombiaDateString(), []);
  const [mesPorDefecto, setMesPorDefecto] = useState(hoy.slice(0, 7));
  const [reporte, setReporte] = useState(undefined); // undefined: asking · null: not this client
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  // "Todo" may open on the last month with data, once: never after the person chose a period.
  const buscoUltimo = useRef(false);
  const avisar = useRef(onReporte);
  avisar.current = onReporte;
  const clave = `${periodo ? `${periodo.desde}|${periodo.hasta}` : mesPorDefecto}|${(oficinas || []).join(",")}`;

  useEffect(() => {
    if (!clientCode) return undefined;
    let activo = true;
    setCargando(true);
    setError(null);
    // Asked again when the server was momentarily busy (`conReintento`), rather than said as a failure.
    conReintento(() => getCierreOperationReport(token, mantisBusinessId, clientCode, pedidoDelComite(periodo || mesPorDefecto, oficinas)))
      .then((r) => {
        if (!activo) return;
        if (r?.success && r.data) {
          const ultimo = ultimoMesConDatos(r.data);
          if (!periodo && !buscoUltimo.current && !r.data.totals?.actual?.tx && ultimo && ultimo !== mesPorDefecto) {
            buscoUltimo.current = true;
            setMesPorDefecto(ultimo);
            return;
          }
          buscoUltimo.current = true;
          setReporte(r.data);
          avisar.current(r.data);
        } else if (r?.errorCode === "NOT_FOUND") {
          setReporte(null);
          avisar.current(null);
        } else {
          setError(correctionError(r) || "No se pudo leer el comité de seguimiento.");
          // The page stops waiting for a report that did not come (its presentation button).
          avisar.current(null);
        }
        setCargando(false);
      })
      .catch(() => {
        if (!activo) return;
        setError("No se pudo leer el comité de seguimiento.");
        avisar.current(null);
        setCargando(false);
      });
    return () => {
      activo = false;
    };
    // `clave` says the period and the offices as one string, so a new array with the same codes asks nothing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, mantisBusinessId, clientCode, clave]);

  const indicadores = useMemo(() => (reporte ? indicadoresDelMes(reporte) : null), [reporte]);
  const porAnio = useMemo(() => transaccionesPorAnio(reporte?.monthly, reporte?.month), [reporte]);
  const dias = useMemo(() => diasDelMes(reporte?.daily), [reporte]);
  const regiones = useMemo(() => (reporte ? porRegional(reporte) : []), [reporte]);
  const flujo = useMemo(() => (reporte ? recaudosYPagos(reporte) : []), [reporte]);
  const top = useMemo(() => (reporte ? topOficinas(reporte, "tx", 10).map((o) => ({ ...o, nombre: nombreCorto(o) })) : []), [reporte]);

  if (reporte === null) return null;
  // Asking for the first time, or the first answer failed: a quiet frame, never the whole page's spinner.
  if (reporte === undefined) {
    if (!error && !cargando) return null;
    return (
      <section className="rounded-[14px] border bg-card p-6 shadow-brinks-card" aria-label="Comité de seguimiento">
        {error ? <p className="text-center text-[12px] text-brinks-muted-foreground">{error}</p> : <div className="grid place-items-center"><Spinner /></div>}
      </section>
    );
  }

  const ant = etiquetaDePeriodo(reporte, "anterior");
  const act = etiquetaDePeriodo(reporte);
  const antCorto = etiquetaDePeriodo(reporte, "anterior", { corta: true });
  const tope = Math.round((Number(reporte.overLimitFactor) - 1) * 100);
  const totalRegiones = regiones.reduce((s, r) => s + r.tx, 0);
  // B-138 — what a click does here: an office or a regional narrows the page; a day opens its closings.
  const elegirOficina = onElegir ? (o) => onElegir({ tipo: "oficina", etiqueta: o.oficina, codigos: [String(o.codigo)] }) : null;
  const elegirRegion = onElegir
    ? (region) => onElegir({
      tipo: "grupo",
      etiqueta: `Regional ${region}`,
      codigos: (reporte.offices || []).filter((o) => (o.region || "Sin regional") === region).map((o) => String(o.officeCode)),
    })
    : null;
  const alTocarDia = onAbrirDia
    ? {
      style: { cursor: "pointer" },
      onClick: (e) => {
        const d = e?.activePayload?.[0]?.payload;
        if (d?.fecha) onAbrirDia(d.fecha);
      },
    }
    : {};
  const pistaDia = onAbrirDia ? " Toca un día para ver sus cierres." : "";
  const totalFlujo = flujo.reduce((s, f) => s + f.valor, 0);

  return (
    <section className={cn("space-y-2 transition-opacity", cargando && "opacity-60")} aria-label="Comité de seguimiento" aria-busy={cargando}>
      <div className="flex flex-wrap items-end justify-between gap-2 pt-2">
        <div>
          <h2 className="font-lora text-[20px] font-semibold text-brinks-foreground">Comité de seguimiento</h2>
          <p className="text-[12.5px] text-brinks-muted-foreground">
            <span className="font-medium text-brinks-foreground">{act}</span> frente a {ant}
            {reporte.selectedOffices ? ` · ${reporte.selectedOffices.length} ${reporte.selectedOffices.length === 1 ? "oficina" : "oficinas"}` : " · todas las oficinas"}
          </p>
        </div>
        {cargando ? <Spinner size="sm" /> : null}
      </div>

      {error ? <p role="alert" className="rounded-[8px] border border-brinks-danger-border bg-brinks-danger-soft px-3 py-2 text-[12px] text-brinks-danger">{error}</p> : null}

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
        <Cifra icono={Repeat2} etiqueta="Transacciones" valor={cuenta(indicadores.tx.actual)} detalle={`${dias.length} días con operación`} variacion={indicadores.tx.variacion} anterior={antCorto} serie={dias} clave="tx" />
        <Cifra icono={Landmark} etiqueta="Montos" valor={compacto(indicadores.valor.actual)} detalle={pesos(indicadores.valor.actual)} variacion={indicadores.valor.variacion} anterior={antCorto} serie={dias} clave="valor" />
        <Cifra icono={Vault} etiqueta="Excesos de cupo" valor={cuenta(indicadores.excesos.actual)} detalle={`días-oficina · ${compacto(indicadores.excesos.valor)}`} variacion={indicadores.excesos.variacion} invertir anterior={antCorto} serie={dias} clave="excesos" />
        <Cifra icono={Undo2} etiqueta="Reversos" valor={cuenta(indicadores.reversos.actual)} detalle={pesos(indicadores.reversos.valor)} variacion={indicadores.reversos.variacion} invertir anterior={antCorto} serie={dias} clave="reversos" />
      </div>

      <Bloque numero={1} titulo="Transacciones y montos procesados" />
      <div className="grid gap-2 xl:grid-cols-3">
        <Tarjeta
          className="xl:col-span-2"
          titulo="Número de transacciones por mes"
          subtitulo={porAnio.comparacion
            ? `${porAnio.comparacion.anio} frente a ${porAnio.comparacion.previo}: ${porAnio.comparacion.diferencia >= 0 ? "+" : ""}${cuenta(porAnio.comparacion.diferencia)} (${porcentaje(porAnio.comparacion.variacion)})`
            : "Los meses con cierres en Mantis, una línea por año."}
        >
          {porAnio.anios.length ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={porAnio.puntos} margin={{ top: 10, right: 12, bottom: 0, left: 0 }}>
                  <CartesianGrid stroke="var(--brinks-border)" vertical={false} />
                  <XAxis dataKey="etiqueta" {...EJE} />
                  <YAxis tickFormatter={compacto} {...EJE} width={44} />
                  <Tooltip content={<Burbuja />} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                  {porAnio.anios.map((anio, i) => (
                    <Line
                      key={anio}
                      type="monotone"
                      dataKey={anio}
                      name={anio}
                      stroke={i === porAnio.anios.length - 1 ? PRIMARIO : SERIE[Math.min(SERIE.length - 1, porAnio.anios.length - i + 1)]}
                      strokeWidth={i === porAnio.anios.length - 1 ? 2.5 : 1.5}
                      dot={{ r: 3 }}
                      connectNulls
                      isAnimationActive={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : <Vacio>Sin transacciones registradas todavía.</Vacio>}
        </Tarjeta>

        <Tarjeta titulo="Recaudos y pagos" subtitulo={`Lo que entró y salió de caja, ${act}.`}>
          {totalFlujo ? (
            <div className="flex h-64 flex-col">
              <div className="relative min-h-0 flex-1">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={flujo} dataKey="valor" nameKey="nombre" innerRadius="62%" outerRadius="88%" paddingAngle={2} stroke="none" isAnimationActive={false}>
                      {flujo.map((f, i) => <Cell key={f.nombre} fill={i ? ANTERIOR : PRIMARIO} />)}
                    </Pie>
                    <Tooltip content={<Burbuja formatos={{ valor: pesos }} />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="pointer-events-none absolute inset-0 grid place-items-center text-center">
                  <div>
                    <p className="text-[18px] font-semibold tabular-nums text-brinks-foreground">{compacto(totalFlujo)}</p>
                    <p className="text-[10.5px] text-brinks-muted-foreground">en caja</p>
                  </div>
                </div>
              </div>
              <ul className="mt-2 space-y-1 text-[12px]">
                {flujo.map((f, i) => (
                  <li key={f.nombre} className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full" style={{ background: i ? ANTERIOR : PRIMARIO }} />
                    <span className="text-brinks-foreground">{f.nombre}</span>
                    <span className="text-brinks-muted-foreground">{cuenta(f.cantidad)} TX</span>
                    <span className="ml-auto font-medium tabular-nums">{pesos(f.valor)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : <Vacio>Sin movimientos de caja.</Vacio>}
        </Tarjeta>
      </div>

      <div className="grid gap-2 xl:grid-cols-3">
        <Tarjeta className="xl:col-span-2" titulo="Tendencia del volumen" subtitulo={`Transacciones (barras) y montos (línea) por día, ${act}.${pistaDia}`}>
          {dias.length ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={dias} margin={{ top: 10, right: 4, bottom: 0, left: 0 }} {...alTocarDia}>
                  <CartesianGrid stroke="var(--brinks-border)" vertical={false} />
                  <XAxis dataKey="dia" {...EJE} interval={0} />
                  <YAxis yAxisId="tx" tickFormatter={compacto} {...EJE} width={40} />
                  <YAxis yAxisId="valor" orientation="right" tickFormatter={compacto} {...EJE} width={56} />
                  <Tooltip content={<Burbuja formatos={{ valor: pesos }} etiqueta={(l, p) => p?.[0]?.payload?.fecha || l} />} cursor={{ fill: "var(--brinks-surface)" }} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                  <Bar yAxisId="tx" dataKey="tx" name="Transacciones" fill={PRIMARIO} radius={[4, 4, 0, 0]} maxBarSize={22} />
                  <Line yAxisId="valor" type="monotone" dataKey="valor" name="Montos" stroke="#6f86c2" strokeWidth={2} dot={false} isAnimationActive={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          ) : <Vacio>Sin transacciones en {act}.</Vacio>}
        </Tarjeta>

        <Tarjeta titulo="Transacciones por regional" subtitulo={`Participación de cada regional, ${act}.${elegirRegion ? " Toca una para ver solo esa." : ""}`}>
          {totalRegiones ? (
            <div className="flex h-64 flex-col">
              <div className="min-h-0 flex-1">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={regiones}
                      dataKey="tx"
                      nameKey="region"
                      innerRadius="55%"
                      outerRadius="88%"
                      paddingAngle={1.5}
                      stroke="none"
                      isAnimationActive={false}
                      style={elegirRegion ? { cursor: "pointer" } : undefined}
                      onClick={elegirRegion ? (d) => d?.region && elegirRegion(d.region) : undefined}
                    >
                      {regiones.map((r, i) => <Cell key={r.region} fill={SERIE[i % SERIE.length]} />)}
                    </Pie>
                    <Tooltip content={<Burbuja formatos={{ tx: cuenta }} />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <ul className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11.5px]">
                {regiones.map((r, i) => (
                  <li key={r.region} className="min-w-0">
                    <button
                      type="button"
                      disabled={!elegirRegion}
                      onClick={() => elegirRegion && elegirRegion(r.region)}
                      title={elegirRegion ? `Ver solo la regional ${r.region} en el Dashboard` : undefined}
                      className="flex w-full min-w-0 items-center gap-1.5 rounded-[5px] px-1 text-left enabled:hover:bg-brinks-surface-hover disabled:cursor-default"
                    >
                      <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: SERIE[i % SERIE.length] }} />
                      <span className="min-w-0 truncate text-brinks-foreground">{r.region}</span>
                      <span className="ml-auto tabular-nums text-brinks-muted-foreground">{Math.round((r.tx / totalRegiones) * 100)} %</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : <Vacio>Sin transacciones en {act}.</Vacio>}
        </Tarjeta>
      </div>

      <div className="grid gap-2 xl:grid-cols-[2fr_3fr]">
        <Tarjeta titulo="Las diez oficinas con más transacciones" subtitulo={`${act}, con ${ant} en gris.${elegirOficina ? " Toca una para ver solo esa." : ""}`}>
          {top.length ? (
            <div className="h-[330px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={top}
                  layout="vertical"
                  margin={{ top: 0, right: 8, bottom: 0, left: 0 }}
                  barGap={1}
                  style={elegirOficina ? { cursor: "pointer" } : undefined}
                  onClick={(e) => {
                    const o = e?.activePayload?.[0]?.payload;
                    if (elegirOficina && o?.codigo) elegirOficina(o);
                  }}
                >
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="nombre" width={130} {...EJE} fontSize={11} />
                  <Tooltip cursor={{ fill: "var(--brinks-surface)" }} content={<Burbuja etiqueta={(l, p) => p?.[0]?.payload?.oficina || l} />} />
                  <Bar dataKey="anterior" name={ant} fill={ANTERIOR} radius={[0, 3, 3, 0]} maxBarSize={9} />
                  <Bar dataKey="actual" name={act} fill={PRIMARIO} radius={[0, 3, 3, 0]} maxBarSize={9} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : <Vacio>Sin transacciones en estos dos periodos.</Vacio>}
        </Tarjeta>
        <PorOficina reporte={reporte} onElegir={elegirOficina} />
      </div>

      <Bloque numero={2} titulo="Excesos de cupo" />
      <Tarjeta titulo="Oficinas en exceso por día" subtitulo={`Cuántas oficinas pasaron el cupo cada día (barras) y por cuánto (línea), ${act}.${pistaDia}`}>
        {dias.some((d) => d.excesos) ? (
          <div className="h-60">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={dias} margin={{ top: 10, right: 4, bottom: 0, left: 0 }} {...alTocarDia}>
                <CartesianGrid stroke="var(--brinks-border)" vertical={false} />
                <XAxis dataKey="dia" {...EJE} interval={0} />
                <YAxis yAxisId="n" allowDecimals={false} {...EJE} width={32} />
                <YAxis yAxisId="valor" orientation="right" tickFormatter={compacto} {...EJE} width={56} />
                <Tooltip content={<Burbuja formatos={{ excesoValor: pesos }} etiqueta={(l, p) => p?.[0]?.payload?.fecha || l} />} cursor={{ fill: "var(--brinks-surface)" }} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                <Bar yAxisId="n" dataKey="excesos" name="Oficinas en exceso" fill={AVISO} radius={[4, 4, 0, 0]} maxBarSize={22} />
                <Bar yAxisId="n" dataKey="sobreLimite" name={`Sobre el ${tope} %`} fill={PELIGRO} radius={[4, 4, 0, 0]} maxBarSize={22} />
                <Line yAxisId="valor" type="monotone" dataKey="excesoValor" name="Valor del exceso" stroke={PRIMARIO} strokeWidth={2} dot={false} isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        ) : <Vacio>Ninguna oficina pasó el cupo en {act}.</Vacio>}
      </Tarjeta>
      <div className="grid gap-2 xl:grid-cols-2">
        <Ranking reporte={reporte} titulo="Ranking de excesos por oficina" subtitulo="Días en que la bóveda superó el cupo, y por cuánto." cantidad="excesos" valor="excesoValor" columnaCantidad="Días" vacio="Ninguna oficina superó el cupo." tono={AVISO} onElegir={elegirOficina} />
        <Ranking reporte={reporte} titulo={`Oficinas que sobrepasan el ${tope} % del cupo`} subtitulo={`Días sobre el ${100 + tope} % del cupo, y lo que pasó de ese tope.`} cantidad="sobreLimite" valor="sobreLimiteValor" columnaCantidad="Días" vacio={`Ninguna oficina pasó el ${tope} % del cupo.`} tono={PELIGRO} onElegir={elegirOficina} />
      </div>

      <Bloque numero={3} titulo="Reversos y transportadora" />
      <div className="grid gap-2 xl:grid-cols-2">
        <Ranking reporte={reporte} titulo="Ranking de reversos por oficina" subtitulo="Transacciones anuladas y su valor." cantidad="reversos" valor="reversosValor" columnaCantidad="Reversos" vacio="Sin reversos." limite={12} tono={PRIMARIO} onElegir={elegirOficina} />
        <Tarjeta
          titulo="Transportadora"
          subtitulo={`Provisiones ${pesos(indicadores.provisiones.actual)} · recolecciones ${pesos(indicadores.recolecciones.actual)} · recaudos BBVA ${pesos(indicadores.bbva.valor)}`}
          extra={<Truck className="h-4 w-4 text-brinks-muted-foreground" />}
        >
          {dias.some((d) => d.provisiones || d.recolecciones) ? (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dias} margin={{ top: 10, right: 4, bottom: 0, left: 0 }} barGap={1} {...alTocarDia}>
                  <CartesianGrid stroke="var(--brinks-border)" vertical={false} />
                  <XAxis dataKey="dia" {...EJE} interval={0} />
                  <YAxis tickFormatter={compacto} {...EJE} width={56} />
                  <Tooltip content={<Burbuja formatos={{ provisiones: pesos, recolecciones: pesos }} etiqueta={(l, p) => p?.[0]?.payload?.fecha || l} />} cursor={{ fill: "var(--brinks-surface)" }} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="provisiones" name="Provisiones" fill={PRIMARIO} radius={[3, 3, 0, 0]} maxBarSize={14} />
                  <Bar dataKey="recolecciones" name="Recolecciones" fill="#93a3c4" radius={[3, 3, 0, 0]} maxBarSize={14} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : <Vacio>Sin movimientos de la transportadora en {act}.</Vacio>}
        </Tarjeta>
      </div>

      <p className="px-1 text-[11px] text-brinks-muted-foreground">
        La atribución de los reversos, las PQRs y los compromisos del comité no vienen en los reportes de Bantotal.
      </p>
    </section>
  );
}

export default ComiteSeguimiento;
